import React, { useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import ScrollTo from 'react-scroll-into-view';
import { useAppSelector } from 'app/hooks';

export const TOP_PROCESS_BAR_HEIGHT = 80;
const HEADER_HEIGHT = 45; // px, adjust if your header is a different height

export interface StepProcessProps {
  title: string;
  percentOnProcess: number;
  specialId?: string;
  maxWidth?: number;
}
interface Props {
  icon?: any;
  title?: string;
  stepProcess: Array<StepProcessProps>;
  process: number;
  barHeight?: number;
  isMobile?: boolean;
}

const TopProcessBar = (props: Props) => {
  const barHeight = props.barHeight || TOP_PROCESS_BAR_HEIGHT;
  const [activeStep, setActiveStep] = useState(0);
  const isMobile = props.isMobile || window.innerWidth <= 768;
  const sidebarCollapsed = useAppSelector((store) => store.appStore.sidebarCollapsed);
  
  // Calculate width based on sidebar state
  const sidebarWidth = sidebarCollapsed ? 130 : 290; // 70px toolbar + 60px mini when collapsed, 290px when expanded
  const topProcessBarWidth = isMobile ? '100vw' : `calc(100vw - ${sidebarWidth}px)`;

  const handleStepClick = (index: number, process: StepProcessProps) => {
    setActiveStep(index);
    // Scroll tới section tương ứng
    const sectionId = process.specialId ?? process.title.replace(/ /g, '').replace('/', '');
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div
      style={{
        zIndex: isMobile ? 998 : 999, // Lower z-index on mobile to not overlap header
        position: 'fixed',
        width: topProcessBarWidth,
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        padding: 0,
        top: isMobile ? '60px' : undefined, // Add top offset for mobile header
        left: isMobile ? '0' : undefined,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: isMobile ? '0 0 12px 12px' : '0 0 20px 20px',
          border: '1px solid #eee',
          padding: isMobile ? '0 12px 8px 12px' : '0 20px 10px 20px',
          margin: 0,
          boxShadow: isMobile ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        {/* Title row */}
        <div
          className="w-100 d-flex align-items-center"
          style={{ 
            minHeight: isMobile ? 32 : 40, 
            padding: isMobile ? '8px 0 0 0' : '10px 0 0 0' 
          }}
        >
          <div className="d-flex align-items-center w-100" >
            {/* <div 
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                background: 'linear-gradient(90deg, #4d88f0 0%, #8cb5f3 100%)',
                marginRight: 10,
                boxShadow: '0 1px 4px rgba(102, 126, 234, 0.15)'
              }}
            >
              <span style={{ color: 'white', fontSize: 16 }}>{props.icon}</span>
            </div> */}
            <span
              style={{
                fontWeight: 700,
                fontSize: isMobile ? 12 : 14,
                background: 'linear-gradient(90deg, #4d88f0 0%, #8cb5f3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: isMobile ? '10px' : '15px',
                display: '-webkit-box',
                WebkitLineClamp: isMobile ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                maxWidth: isMobile ? '100%' : undefined,
              }}
            >
              {props.title}
            </span>
          </div>
        </div>
        {/* Stepper bar row */}
        <div
          className="w-100 d-flex justify-content-center align-items-center"
          style={{ 
            minHeight: isMobile ? 40 : 48, 
            marginTop: 2, 
            marginBottom: isMobile ? '12px' : '20px', 
            padding: isMobile ? '0 8px 0 8px' : '0 20px 0 20px' 
          }}
        >
          <div
            className="flex-grow-1 d-flex justify-content-center align-items-center"
            style={{
              gap: 0,
              position: 'relative',
              height: isMobile ? 40 : 48,
              borderBottomLeftRadius: isMobile ? 6 : 8,
              borderBottomRightRadius: isMobile ? 6 : 8,
              padding: '0',
            }}
          >
            {props.stepProcess.map((process, index) => (
              <React.Fragment key={index}>
                {/* Line trước node */}
                {index !== 0 && (
                  <div
                    style={{
                      flex: 1,
                      height: 2,
                      background:
                        index <= activeStep
                          ? 'linear-gradient(90deg, #4d88f0 0%, #8cb5f3 100%)'
                          : '#e5e7eb',
                      transition: 'background 0.3s',
                      margin: '0 0',
                      alignSelf: 'center',
                      position: 'relative',
                      top: 0,
                      bottom: 0,
                    }}
                  />
                )}
                {/* Node */}
                <div
                  className="d-flex flex-column align-items-center"
                  style={{ 
                    minWidth: isMobile ? 80 : 120, 
                    cursor: 'pointer', 
                    justifyContent: 'center',
                    padding: isMobile ? '0 4px' : '0'
                  }}
                  onClick={() => handleStepClick(index, process)}
                >
                  <div
                    style={{
                      width: isMobile ? 18 : 22,
                      height: isMobile ? 18 : 22,
                      borderRadius: '50%',
                      background:
                        index === activeStep
                          ? 'linear-gradient(90deg, #4d88f0 0%, #8cb5f3 100%)'
                          : '#fff',
                      border: `${isMobile ? '1.5px' : '2px'} solid ${index <= activeStep ? '#4d88f0' : '#cbd5e1'}`,
                      color: index === activeStep ? '#fff' : '#764ba2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: isMobile ? 10 : 13,
                      boxShadow:
                        index === activeStep ? '0 1px 4px rgba(102, 126, 234, 0.15)' : 'none',
                      transition: 'all 0.2s',
                    }}
                  >
                    {index + 1}
                  </div>
                  <span
                    style={{
                      marginTop: isMobile ? 4 : 6,
                      color: index === activeStep ? '#4d88f0' : '#64748b',
                      fontWeight: index === activeStep ? 700 : 500,
                      fontSize: isMobile ? 10 : 13,
                      textAlign: 'center',
                      maxWidth: isMobile ? 70 : (process.maxWidth ? process.maxWidth : 120),
                      lineHeight: isMobile ? 1.1 : 1.2,
                      transition: 'color 0.2s',
                      display: '-webkit-box',
                      WebkitLineClamp: isMobile ? 2 : 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {process.title}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 768px) {
            .gm-section-card, .top-process-bar-fix {
              padding-left: 8px !important;
              padding-right: 8px !important;
            }
            .top-process-bar-mobile {
              position: fixed;
              top: 60px; /* Position below mobile header */
              left: 0;
              right: 0;
              z-index: 998; /* Below header z-index */
              background: #fff;
              border-bottom: 1px solid #eee;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            .top-process-bar-mobile .step-title {
              font-size: 10px !important;
              line-height: 1.1 !important;
              max-width: 70px !important;
            }
            .top-process-bar-mobile .step-node {
              width: 18px !important;
              height: 18px !important;
              font-size: 10px !important;
            }
            /* Ensure main content has proper top margin when TopProcessBar is present */
            .main-container.gm-basic-info-page {
              margin-top: 60px; /* Adjust based on TopProcessBar height */
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default TopProcessBar;
